/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Real Estate dashboard — GET /re/dashboard.
 *
 * Each block (inventory, sales, viewings, commissions, leasing, mortgages) is
 * null when the tenant does not hold the module, and is then hidden.
 * Charts use react-chartjs-2, as layouts/analytics/ReportChart does.
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import {
  ArcElement, BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import InfoCard from './components/ReInfoCard';
import RePage, { LoadError } from './components/RePage';
import { KpiTile } from './components/Widgets';
import { UNIT_TILE_COLORS, TONES, toneFor } from './components/statusColors';
import {
  MORTGAGE_STAGES, UNIT_STATUSES, formatMoney, formatNumber, formatPct, humanize, useReGet,
} from './api';
import { defaultCurrency, isAustralia } from '../variant';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const chartBox = { position: 'relative', height: 220 };

function InventoryChart({ inventory }) {
  const data = useMemo(() => {
    const statuses = UNIT_STATUSES.filter((s) => (inventory[s] || 0) > 0);
    return {
      labels: statuses.map(humanize),
      datasets: [{
        data: statuses.map((s) => inventory[s] || 0),
        backgroundColor: statuses.map((s) => UNIT_TILE_COLORS[s].border),
        borderWidth: 0,
      }],
    };
  }, [inventory]);
  if (!data.labels.length) {
    return <MDTypography variant="button" color="text">No units yet.</MDTypography>;
  }
  return (
    <MDBox sx={chartBox}>
      <Doughnut
        data={data}
        options={{ maintainAspectRatio: false, cutout: '62%', plugins: { legend: { position: 'right', labels: { boxWidth: 12 } } } }}
      />
    </MDBox>
  );
}

function MortgageChart({ byStage }) {
  const data = useMemo(() => ({
    labels: MORTGAGE_STAGES.map(humanize),
    datasets: [{
      label: 'Applications',
      data: MORTGAGE_STAGES.map((s) => byStage[s] || 0),
      backgroundColor: MORTGAGE_STAGES.map((s) => TONES[toneFor('mortgage', s)].dot),
      borderRadius: 4,
    }],
  }), [byStage]);
  return (
    <MDBox sx={chartBox}>
      <Bar
        data={data}
        options={{
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
        }}
      />
    </MDBox>
  );
}

function CommissionChart({ commissions }) {
  const keys = ['accrued', 'eligible', 'payable'];
  const data = {
    labels: keys.map(humanize),
    datasets: [{
      label: defaultCurrency(),
      data: keys.map((k) => Number(commissions[k] || 0)),
      backgroundColor: [TONES.neutral.dot, TONES.info.dot, TONES.warning.dot],
      borderRadius: 4,
    }],
  };
  return (
    <MDBox sx={{ ...chartBox, height: 160 }}>
      <Bar
        data={data}
        options={{
          indexAxis: 'y',
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => formatMoney(ctx.parsed.x) } },
          },
          scales: { x: { beginAtZero: true, ticks: { callback: (v) => formatMoney(v, defaultCurrency(), { compact: true }) } } },
        }}
      />
    </MDBox>
  );
}

export default function ReDashboard() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useReGet('/re/dashboard');
  const d = data || {};

  const inventoryTotal = d.inventory ? Object.values(d.inventory).reduce((a, b) => a + (Number(b) || 0), 0) : 0;
  const available = d.inventory ? d.inventory.AVAILABLE || 0 : 0;
  const nothing = !loading && data && !d.inventory && !d.sales && !d.viewings && !d.commissions && !d.leasing && !d.mortgages;

  return (
    <RePage
      title="Real Estate"
      subtitle="Inventory, sales, brokerage, leasing and mortgages at a glance"
      actions={(
        <>
          {d.inventory && (
            <MDButton variant="outlined" color="info" size="small" onClick={() => navigate('/real-estate/inventory')}>
              Inventory grid
            </MDButton>
          )}
          {d.sales && (
            <MDButton variant="gradient" color="info" size="small" onClick={() => navigate('/real-estate/bookings/new')}>
              New booking
            </MDButton>
          )}
        </>
      )}
    >
      <LoadError error={error} onRetry={reload} />
      {nothing && (
        <MDTypography variant="button" color="text">
          No Real Estate modules are enabled for this workspace.
        </MDTypography>
      )}

      {(loading || d.sales) && (
        <MDBox mb={3}>
          <MDTypography variant="h6" mb={1.5}>Sales</MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={2.4}>
              <KpiTile loading={loading} label="Bookings (MTD)" icon="receipt_long" value={formatNumber(d.sales?.bookings_mtd)} onClick={() => navigate('/real-estate/bookings')} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <KpiTile loading={loading} label="Sales value (MTD)" icon="payments" value={formatMoney(d.sales?.value_mtd, defaultCurrency(), { compact: true })} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <KpiTile loading={loading} label="Collected (MTD)" icon="account_balance" tone="success" value={formatMoney(d.sales?.collected_mtd, defaultCurrency(), { compact: true })} />
            </Grid>
            <Grid item xs={12} sm={6} lg={2.4}>
              <KpiTile loading={loading} label="Overdue" icon="warning" tone="error" value={formatMoney(d.sales?.overdue_amount, defaultCurrency(), { compact: true })} hint={`${formatNumber(d.sales?.overdue_count)} installments`} />
            </Grid>
            {d.viewings && (
              <Grid item xs={12} sm={6} lg={2.4}>
                <KpiTile label="Viewings (next 7d)" icon="event" value={formatNumber(d.viewings.upcoming_7d)} hint={`${formatNumber(d.viewings.completed_30d)} done in 30d · ${formatPct(d.viewings.conversion_30d)} converted`} onClick={() => navigate('/real-estate/viewings')} />
              </Grid>
            )}
          </Grid>
        </MDBox>
      )}

      <Grid container spacing={2}>
        {d.inventory && (
          <Grid item xs={12} lg={6}>
            <InfoCard
              icon="apartment"
              title="Inventory"
              headerAction={<MDTypography variant="caption" color="text">{`${formatNumber(available)} of ${formatNumber(inventoryTotal)} available`}</MDTypography>}
            >
              <InventoryChart inventory={d.inventory} />
            </InfoCard>
          </Grid>
        )}
        {d.commissions && (
          <Grid item xs={12} lg={6}>
            <InfoCard icon="handshake" title="Broker commissions" headerAction={(
              <MDButton size="small" variant="text" color="info" onClick={() => navigate('/real-estate/commissions')}>Ledger</MDButton>
            )}
            >
              <Grid container spacing={1.5} mb={1}>
                <Grid item xs={4}><KpiTile label="Accrued" tone="neutral" value={formatMoney(d.commissions.accrued, defaultCurrency(), { compact: true })} /></Grid>
                <Grid item xs={4}><KpiTile label="Eligible" tone="info" value={formatMoney(d.commissions.eligible, defaultCurrency(), { compact: true })} /></Grid>
                <Grid item xs={4}><KpiTile label="Payable" tone="warning" value={formatMoney(d.commissions.payable, defaultCurrency(), { compact: true })} /></Grid>
              </Grid>
              <CommissionChart commissions={d.commissions} />
            </InfoCard>
          </Grid>
        )}
        {d.leasing && (
          <Grid item xs={12} lg={6}>
            <InfoCard icon="key" title="Leasing">
              <Grid container spacing={1.5}>
                <Grid item xs={6}><KpiTile label="Active leases" tone="success" value={formatNumber(d.leasing.active)} onClick={() => navigate('/real-estate/leases')} /></Grid>
                <Grid item xs={6}><KpiTile label="Expiring in 90 days" tone="warning" value={formatNumber(d.leasing.expiring_90d)} onClick={() => navigate('/real-estate/leases?expiring_within_days=90')} /></Grid>
                <Grid item xs={6}><KpiTile label={isAustralia() ? 'Rent due (30d)' : 'Cheques due (30d)'} tone="info" value={formatNumber(d.leasing.cheques_due_30d)} onClick={() => navigate('/real-estate/rent-schedule')} /></Grid>
                <Grid item xs={6}><KpiTile label={isAustralia() ? 'Dishonoured payments' : 'Bounced cheques'} tone="error" value={formatNumber(d.leasing.bounced)} onClick={() => navigate('/real-estate/rent-schedule?status=BOUNCED')} /></Grid>
              </Grid>
            </InfoCard>
          </Grid>
        )}
        {d.mortgages && (
          <Grid item xs={12} lg={6}>
            <InfoCard icon="account_balance" title="Mortgage pipeline" headerAction={(
              <MDButton size="small" variant="text" color="info" onClick={() => navigate('/real-estate/mortgage-applications')}>Pipeline</MDButton>
            )}
            >
              <MortgageChart byStage={d.mortgages.by_stage || {}} />
            </InfoCard>
          </Grid>
        )}
      </Grid>
    </RePage>
  );
}
